// Ubicación en el repo: src/lib/catalog.js
// Catálogo de inventario (audífonos y otros insumos) — tabla `catalog`.
// Solo Admin puede escribir (RLS `catalog_insert`/`catalog_update`:
// is_admin() or is_bootstrap_mode()); la lectura es abierta a cualquier
// autenticado (necesaria para el selector de marca/modelo del formulario
// de venta de audífonos). Prerrequisito del módulo Audífonos:
// `hearingaids.catalog_id` no admite null.
//
// Fase A del módulo de Inventario/Contabilidad (migración 0046, 24-ago-2026,
// ver plan-inventario-y-contabilidad.md): `type`/`brand` (texto libre/enum
// fijo) quedan reemplazados por `category_id`/`brand_id` (entidades
// propias, ver categories.js/brands.js), más `supplier_id`/`cost_price`/
// `sku` nuevos. IMPORTANTE — por qué `createCatalogItem`/`updateCatalogItem`
// siguen escribiendo `type`/`brand` con el nombre de la categoría/marca
// elegida, en vez de dejarlos vacíos: `HearingAidFormModal.jsx` (venta de
// audífonos, un flujo en uso real) todavía agrupa por `c.brand` (texto) —
// no se tocó esa pantalla en esta fase por ser un flujo de venta activo,
// así que estas dos columnas se mantienen sincronizadas automáticamente
// para no romperla. (El filtro por `c.type === 'Audífono'` que existía acá
// se reemplazó por `categorySpecialRole`/`special_role` en la migración
// 0050 — ver más abajo — así que ya no depende del nombre de la
// categoría, solo `brand` sigue siendo texto libre.) Se puede migrar
// HearingAidFormModal a usar category_id/brand_id directamente en una
// pasada aparte, más adelante, sin apuro.
//
// Rol especial de categoría (migración 0050, 25-ago-2026): la venta de
// audífonos dependía de que la categoría se llamara EXACTAMENTE "Audífono"
// — reemplazado por `categories.special_role = 'audifono'`, un flag
// explícito e independiente del nombre. `listCatalog({ categorySpecialRole })`
// filtra por ese flag (consulta aparte a `categories`, no un embedding
// filtrado — ver el porqué en el comentario de la función). Ver también
// CategoryFormModal.jsx (dónde se marca la categoría) y
// CatalogFormModal.jsx (`isHearingAid`, mismo flag).
//
// Fase B del módulo de Inventario/Contabilidad (migración 0049, 25-ago-2026):
// `catalog.stock`/`catalog.stock_min` (un solo número global) quedan
// congeladas como respaldo histórico — el stock real ahora vive en
// `catalog_branch_stock` (una fila por producto y por sucursal). Por eso
// `listCatalog({ branchId })` recibe un `branchId` opcional: si se pasa,
// sobrescribe `.stock`/`.stock_min` de cada producto con el valor de ESA
// sucursal (0/2 por defecto si esa sucursal todavía no tiene fila propia),
// así el resto del código (HearingAidFormModal, Catalog.jsx, Dashboard)
// sigue leyendo `item.stock` como siempre, sin enterarse del cambio de
// modelo de datos por debajo. Si no se pasa `branchId`, se devuelven los
// valores globales congelados (compatibilidad con cualquier llamado viejo
// que no se haya actualizado).
import { supabase } from './supabaseClient';

// Sintaxis de embedding de PostgREST: alias:tabla_relacionada(columnas) —
// no alias:columna_fk(columnas) (error real encontrado y corregido antes
// de desplegar esta fase). Sin necesidad de `!fk_column` para desambiguar
// porque category_id/brand_id/supplier_id son la única FK de catalog hacia
// cada una de esas tablas — mismo criterio que `patients(...)` ya usa en
// appointments.js cuando no hay ambigüedad (ver `professionals!professional_id`
// ahí mismo para el caso contrario, con dos FKs al mismo destino).
const CATALOG_SELECT =
  '*, category:categories(id, name, special_role), brand_ref:brands(id, name), supplier:suppliers(id, name)';

// `type` (texto legado) y `categoryId` (entidad nueva) son filtros
// independientes; la pantalla de Catálogo (Catalog.jsx) filtra por
// `categoryId` desde la Fase A (24-ago-2026), ya no por `type`, para que
// las categorías nuevas que se vayan creando aparezcan solas en el filtro
// sin tocar código. `categorySpecialRole` (migración 0050, 25-ago-2026) es
// lo que usa HearingAids.jsx en vez de `type: 'Audífono'` — filtra por el
// flag `categories.special_role`, no por el nombre de la categoría (ver
// CategoryFormModal.jsx). Consulta aparte a `categories` en vez de un
// embedding filtrado (`!inner` + `.eq('category.special_role', ...)`) por
// el mismo motivo que el merge de stock de abajo: acá conviene control
// total y ya hubo un bug real en este archivo por confiar en sintaxis de
// embedding de PostgREST sin probarla (ver DECISIONS.md #106).
export async function listCatalog({ type, categoryId, categorySpecialRole, branchId } = {}) {
  let query = supabase
    .from('catalog')
    .select(CATALOG_SELECT)
    .is('deleted_at', null)
    .order('brand', { ascending: true });
  if (type && type !== 'Todos') query = query.eq('type', type);
  if (categoryId) query = query.eq('category_id', categoryId);
  if (categorySpecialRole) {
    const { data: cats, error: catError } = await supabase
      .from('categories')
      .select('id')
      .eq('special_role', categorySpecialRole)
      .is('deleted_at', null);
    if (catError) throw catError;
    const roleCategoryIds = (cats ?? []).map((c) => c.id);
    if (roleCategoryIds.length === 0) return [];
    query = query.in('category_id', roleCategoryIds);
  }
  const { data, error } = await query;
  if (error) throw error;
  const items = data ?? [];
  if (!branchId) return items;

  // Sobrescribe stock/stock_min con el valor de esta sucursal — ver nota
  // de cabecera. Consulta aparte (no embedding) porque acá conviene tener
  // control total del "default 0/2 si no existe fila todavía", que un
  // embedding LEFT JOIN de PostgREST no resuelve tan directo.
  const { data: branchStock, error: stockError } = await supabase
    .from('catalog_branch_stock')
    .select('catalog_id, stock, stock_min')
    .eq('branch_id', branchId);
  if (stockError) throw stockError;
  const stockById = Object.fromEntries((branchStock ?? []).map((s) => [s.catalog_id, s]));
  return items.map((item) => ({
    ...item,
    stock: stockById[item.id]?.stock ?? 0,
    stock_min: stockById[item.id]?.stock_min ?? 2,
  }));
}

// Stock de un producto en TODAS las sucursales a las que el usuario actual
// tiene acceso — usado por TransferStockModal para elegir origen/destino
// de un traspaso con el número real de cada una a la vista.
export async function listBranchStock(catalogId) {
  const { data, error } = await supabase
    .from('catalog_branch_stock')
    .select('branch_id, stock, stock_min, branches(name)')
    .eq('catalog_id', catalogId);
  if (error) throw error;
  return data ?? [];
}

// Devuelve el id de la fila creada — lo necesita CatalogFormModal para
// escribir su stock inicial en catalog_branch_stock (Fase B) justo después
// de crear el producto, ya que el stock ya no vive en esta tabla.
export async function createCatalogItem(values) {
  const { data, error } = await supabase.from('catalog').insert(values).select('id').single();
  if (error) throw error;
  return data.id;
}

export async function updateCatalogItem(id, values) {
  const { error } = await supabase.from('catalog').update(values).eq('id', id);
  if (error) throw error;
}

// Ajuste manual desde la pantalla de Catálogo (botón "Ajustar stock"): se
// indica el total nuevo directamente (no un delta), igual que
// adjustStockPrompt() del prototipo — pero ahora acotado a una sucursal.
// El descuento/restitución automático al vender/editar/eliminar un
// audífono lo sigue haciendo el trigger de la base (fn_adjust_branch_stock,
// migración 0049), esto es solo para el ajuste manual ("llegó mercadería
// nueva", conteo físico, etc.).
export async function setBranchStock(catalogId, branchId, newStock, stockMin) {
  const { error } = await supabase.rpc('set_branch_stock', {
    p_catalog_id: catalogId,
    p_branch_id: branchId,
    p_stock: Math.max(0, Number(newStock) || 0),
    p_stock_min: stockMin != null && stockMin !== '' ? Number(stockMin) : null,
  });
  if (error) throw new Error(error.message || 'No se pudo ajustar el stock.');
}

// Traspaso atómico entre dos sucursales (migración 0049) — baja en origen,
// alta en destino y registro en stock_transfers, todo del lado del
// servidor (transfer_stock, SECURITY DEFINER) para que no pueda quedar el
// stock a medio camino si algo falla.
export async function transferStock({ catalogId, fromBranchId, toBranchId, quantity, note }) {
  const { error } = await supabase.rpc('transfer_stock', {
    p_catalog_id: catalogId,
    p_from_branch_id: fromBranchId,
    p_to_branch_id: toBranchId,
    p_quantity: Number(quantity) || 0,
    p_note: note || null,
  });
  if (error) throw new Error(error.message || 'No se pudo traspasar el stock.');
}

export async function softDeleteCatalogItem(id, actorId) {
  const { error } = await supabase
    .from('catalog')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

// Papelera de Catálogo (auditoría técnica 11-sep-2026, hallazgo #2,
// DECISIONS.md #120) — a diferencia de Pacientes/Sucursales/Marcas/
// Categorías/Proveedores/Profesionales, un producto eliminado acá no tenía
// forma de recuperarse desde la pantalla. Mismo patrón simple que
// brands.js/categories.js/suppliers.js (restaurar directo) en vez del
// patrón más elaborado de patients.js ("forzar eliminación con motivo") —
// un producto no tiene el tipo de historial clínico asociado que justifica
// esa complejidad extra. `catalog_select` (RLS) ya permite a un Admin leer
// filas con `deleted_at` no nulo, así que no hace falta ningún cambio de
// políticas — ver `listCatalog()` arriba, que sí sigue filtrando
// `deleted_at is null` para el listado normal.
export async function listDeletedCatalogItems() {
  const { data, error } = await supabase
    .from('catalog')
    .select(CATALOG_SELECT)
    .not('deleted_at', 'is', null)
    .order('deleted_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function restoreCatalogItem(id) {
  const { error } = await supabase.from('catalog').update({ deleted_at: null, deleted_by: null }).eq('id', id);
  if (error) throw error;
}
